// EFAM Product Data — Direct to Public Sales Only
// Canonical source: EFAM Launch Master, 07/09/2026.
// Names, prices and warranty tiers are TJ-approved. Do not restore older
// working names or prices from earlier spreadsheets — see the Launch Master.
// IDs 1-24 are fixed. Do not renumber. Do not restore the retired T1-T17 names.
//
// PHOTO ORDER: the FIRST entry in `images` is the card thumbnail and must be
// a forward-facing shot of the machine (slightly off-centre is fine, rear and
// side views are not). Angles chosen 08/09/2026 from the supplier sets.
//
// FINISH: the actual physical colour of the chassis, which is NOT always what
// the variant name suggests. Record a finish only from a photograph you have
// actually looked at. Leave it out rather than inferring one from the name —
// the field simply does not render when absent.
//
// IMAGERY: the 1000x1000 supplier photographs are the primary web imagery.
// The older T1-T17 "4K" files are upscaled from very small source JPGs and are
// archive material only — do not wire them in. Pixel count is not quality.

export const contactInfo = {
  name: "Timothy Holt",
  email: "efamfitness@gmail.com",
  phone: "0490 792 050",
  phoneHours: "9am-5pm",
  whatsappLink: "https://wa.me/61490792050"
};

export const brandMottoes = [
  "1% better every day",
  "Do the right thing for the right reasons",
  "Always learning, ever evolving"
];

// Warranty tiers — under $3,000 AUD = 1 year, $3,000+ = 2 years.
// Extensions: +10% of item price for years 2-3, +15% further for 5 years.
export const warrantyFor = (priceAud) => (priceAud >= 3000 ? 2 : 1);

// IMAGE CACHE VERSION.
// Product photos use stable filenames, so a browser that cached a bad or old
// response keeps it until the cache entry expires. Bump this number whenever a
// photo is replaced under the same filename, or to force every visitor past a
// poisoned cache entry immediately. It is appended to every product image URL.
export const IMAGE_VERSION = '2';

// Build the image list for a product folder from an explicit angle order.
const shots = (slug, order, ext = 'jpg') =>
  order.map((n) => `/images/products/${slug}/${n}.${ext}?v=${IMAGE_VERSION}`);

// ── FEATURED SIX ──────────────────────────────────────────────────────────
export const featuredIds = [1, 4, 7, 10, 13, 24];

// ── THE RANGE — 24 items ──────────────────────────────────────────────────
// 3 standalone Sculpt Seats (1-3) plus 21 EMS machines.
const range = [
  { id: 1, name: "EFAM Sculpt Seat — Onyx", subtitle: "EMS sculpting seat cushion, onyx", price: "$1,299", priceAud: 1299,
    finish: "Black",
    images: [`/images/range/item_01.png?v=${IMAGE_VERSION}`] },
  { id: 2, name: "EFAM Sculpt Seat — Pearl", subtitle: "EMS sculpting seat cushion, pearl", price: "$1,299", priceAud: 1299,
    images: [`/images/range/item_02.png?v=${IMAGE_VERSION}`] },
  { id: 3, name: "EFAM Sculpt Seat — Azure", subtitle: "EMS sculpting seat cushion, azure", price: "$1,299", priceAud: 1299,
    images: [`/images/range/item_03.png?v=${IMAGE_VERSION}`] },
  { id: 4, name: "EFAM SoloForge — Flat", subtitle: "Compact single-handle EMS unit, flat paddle", price: "$1,999", priceAud: 1999,
    finish: "Grey / White",
    images: shots('soloforge-flat', [3, 1, 2, 4, 5, 6, 7]) },
  { id: 5, name: "EFAM SoloForge — Curve", subtitle: "Compact single-handle EMS unit, curved paddle", price: "$1,999", priceAud: 1999,
    images: [`/images/range/item_05.png?v=${IMAGE_VERSION}`] },
  { id: 6, name: "EFAM SoloForge Sculpt Seat", subtitle: "SoloForge unit with the Sculpt Seat cushion", price: "$2,099", priceAud: 2099,
    images: [`/images/range/item_06.png?v=${IMAGE_VERSION}`] },
  { id: 7, name: "EFAM DualForge — Onyx", subtitle: "Compact dual-handle EMS unit", price: "$2,499", priceAud: 2499,
    finish: "White / Blue",
    images: shots('dualspark-onyx', [3, 1, 2, 4, 5, 6]) },
  { id: 8, name: "EFAM DualForge — Volt", subtitle: "Compact dual-handle EMS unit", price: "$2,499", priceAud: 2499,
    finish: "White / Grey",
    images: shots('dualspark-neon', [2, 1, 3, 4, 5, 6]) },
  { id: 9, name: "EFAM DualForge — Arctic", subtitle: "Compact dual-handle EMS unit", price: "$2,499", priceAud: 2499,
    finish: "Black / Gold",
    images: shots('dualspark-arctic', [3, 1, 2, 4, 5, 6]) },
  { id: 10, name: "EFAM DualForge Pro — Signature", subtitle: "Dual-handle EMS unit — TJ's own machine", price: "$3,799", priceAud: 3799,
    finish: "White / Black / Gold",
    images: shots('dualmaster-signature', [2, 3, 4, 1, 5, 6]) },
  { id: 11, name: "EFAM DualForge Pro — Midnight", subtitle: "Dual-handle EMS unit", price: "$3,799", priceAud: 3799,
    finish: "Grey / White",
    images: shots('dualmaster-midnight', [5, 3, 4, 2, 1, 6]) },
  { id: 12, name: "EFAM DualForge Max", subtitle: "Larger dual-handle EMS unit", price: "$5,449", priceAud: 5449,
    finish: "White / Gold",
    images: shots('dualbeast-xl', [5, 3, 4, 2, 1, 6]) },
  { id: 13, name: "EFAM QuadForge Studio", subtitle: "Four-handle EMS system for studios", price: "$6,999", priceAud: 6999,
    images: [`/images/range/item_13.png?v=${IMAGE_VERSION}`] },
  { id: 14, name: "EFAM QuadForge Mobile — Graphite", subtitle: "Four-handle wheeled EMS system", price: "$7,449", priceAud: 7449,
    finish: "Black",
    images: shots('quadroll-pro', [2, 1, 6, 5, 3, 4]) },
  { id: 15, name: "EFAM QuadForge Mobile — Frost", subtitle: "Four-handle wheeled EMS system", price: "$7,449", priceAud: 7449,
    finish: "Grey / White",
    images: shots('quadroll-frost', [2, 3, 4, 1, 5, 6]) },
  { id: 16, name: "EFAM QuadForge — Slate", subtitle: "Four-handle EMS system, desktop or floor", price: "$7,199", priceAud: 7199,
    images: [`/images/range/item_16.png?v=${IMAGE_VERSION}`] },
  { id: 17, name: "EFAM QuadForge — Ivory", subtitle: "Four-handle EMS system, desktop or floor", price: "$7,199", priceAud: 7199,
    images: [`/images/range/item_17.png?v=${IMAGE_VERSION}`] },
  { id: 18, name: "EFAM Meridian", subtitle: "Four-handle wheeled commercial EMS system", price: "$10,999", priceAud: 10999,
    finish: "Ivory / Grey",
    images: shots('quadcommercial-rolling', [3, 4, 5, 2, 1, 6]) },
  { id: 19, name: "EFAM Meridian Onyx", subtitle: "Four-handle wheeled commercial EMS system", price: "$11,999", priceAud: 11999,
    finish: "White / Slate",
    images: shots('quadcommercial-onyx', [3, 4, 5, 2, 1, 6]) },
  { id: 20, name: "EFAM QuadForge Elite", subtitle: "Advanced four-handle wheeled EMS system", price: "$9,199", priceAud: 9199,
    finish: "Bright Blue",
    images: shots('advancedquad-elitemotion', [4, 2, 3, 1]) },
  { id: 21, name: "EFAM QuadForge Elite — Graphite", subtitle: "Advanced four-handle wheeled EMS system", price: "$8,999", priceAud: 8999,
    finish: "Black / White / Gold",
    images: shots('advancedquad-graphite', [3, 4, 5, 2, 1, 6]) },
  { id: 22, name: "EFAM Sovereign", subtitle: "Elite four-handle wheeled EMS system", price: "$9,449", priceAud: 9449,
    finish: "Black / White / Gold",
    images: shots('sovereign-quad', [3, 4, 5, 2, 1, 6]) },
  { id: 23, name: "EFAM Magnum Opus", subtitle: "High-end commercial four-handle EMS system", price: "$15,449", priceAud: 15449,
    finish: "White / Black / Gold",
    images: shots('magnum-opus', [2, 3, 4, 5, 1, 6]) },
  { id: 24, name: "EFAM Crown Jewel", subtitle: "Premier flagship four-handle EMS system", price: "$16,999", priceAud: 16999,
    finish: "Black",
    images: shots('crownjewel-flagship', [2, 1, 3, 5, 4, 6]) }
];

// `image` is always the first angle, so the card, the gallery and the
// structured data can never disagree about which shot represents a product.
export const rangeProducts = range.map((p) => ({ ...p, image: p.images[0] }));

// Homepage featured selection, resolved from the range above.
export const products = featuredIds.map((fid) => rangeProducts.find((p) => p.id === fid));

// ── ACCESSORIES ───────────────────────────────────────────────────────────
export const accessories = [
  { id: 1, name: "EFAM Flat Paddle Pair", subtitle: "Pair of wide flat paddles", price: "$1,300", priceAud: 1300,
    image: "https://customer-assets.emergentagent.com/job_f0a5faf6-98e8-44c8-9627-c4f57b0dfbaf/artifacts/h2ou1wg5_0e821fac-c3be-4fa4-afe0-48be57b617cc-1_all_5427.jpg" },
  { id: 2, name: "EFAM Curved Paddle Pair", subtitle: "Pair of curved upright paddles", price: "$1,300", priceAud: 1300,
    image: "https://customer-assets.emergentagent.com/job_f0a5faf6-98e8-44c8-9627-c4f57b0dfbaf/artifacts/qlh5ws1t_20828.jpg" },
  { id: 3, name: "EFAM Sculpt Seat Cushion", subtitle: "EMS sculpting seat cushion", price: "$1,300", priceAud: 1300,
    image: "https://customer-assets.emergentagent.com/job_f0a5faf6-98e8-44c8-9627-c4f57b0dfbaf/artifacts/9dfuujcu_20768.jpg" }
];

export const faqs = [
  { id: 1, question: "How do I purchase a machine?",
    answer: "Choose a system and use its secure purchase button, or contact EFAM by WhatsApp, email or phone if you want help comparing models before ordering" },
  { id: 2, question: "What's included with purchase?",
    answer: "Every EFAM machine comes with full setup guidance, training materials, and ongoing support through the EFAM Companion App. We make sure you're confident using your system from day one" },
  { id: 3, question: "What is EMS technology?",
    answer: "EMS (Electrical Muscle Stimulation) delivers controlled impulses that activate muscle contractions. Outcomes depend on the equipment, settings, placement, program and individual. EMS should complement—not replace—appropriate exercise and professional advice" },
  { id: 4, question: "What does the RF do?",
    answer: "Some EFAM systems combine EMS with radio frequency (RF). RF is designed to warm targeted tissue and may support the appearance of skin firmness. Individual outcomes vary and no medical result is guaranteed" },
  { id: 5, question: "Is this safe?",
    answer: "Suitability depends on the person and the specific system. EMS and RF are not suitable for everyone, including some people with implanted electronic devices, pregnancy or certain medical conditions. Read the safety guidance and consult a qualified health professional if unsure" },
  { id: 6, question: "When will I see results?",
    answer: "Results vary by individual, settings, placement, frequency of use, nutrition and activity. EFAM does not guarantee body-composition, strength or appearance outcomes" },
  { id: 7, question: "How fast is shipping?",
    answer: "EMS machines and accessories are available to order now. Orders are dispatched in 2-4 days, then ship Express worldwide with an estimated 2-4 week delivery window for machines and 1-3 weeks for accessories. Dispatch and delivery are two separate stages. The displayed price includes shipping; destination customs duties, taxes or import charges may still apply" },
  { id: 8, question: "What warranty do I get?",
    answer: "Machines under $3,000 include 1 year of EFAM warranty; machines $3,000 and above include 2 years. Cover can be extended to years 2-3 for 10% of the item price, and out to 5 years for a further 15%. Warranty does not cover physical damage, water damage, or any damage that is not a machine fault. EFAM warranty is in addition to your rights under the Australian Consumer Law, which are not limited by it" },
  { id: 9, question: "Can I buy a red-light system yet?",
    answer: "Not yet. Red light is coming soon. We are still finalising suppliers, so no specifications or pricing are published and nothing can be ordered. Register your interest and we will contact you when the confirmed range and launch pricing are ready. Everything else on this site — the EMS machines and accessories — is available to order today" },
  { id: 10, question: "Does the model name tell me the colour?",
    answer: "Not always. Model names such as Onyx, Volt, Arctic and Frost are range names, not colour descriptions. Every product card and photo viewer shows the actual finish of the machine you are ordering — for example the DualForge Onyx is a white and blue machine. Go by the finish shown on the card and by the photographs, and ask us if anything is unclear before you order" }
];

export const brandAssets = {
  logo: "https://customer-assets.emergentagent.com/job_ems-reimagined/artifacts/urc40s1m_8637.png",
  hero: "https://customer-assets.emergentagent.com/job_ems-reimagined/artifacts/em6567jv_7579.png",
  values: "https://customer-assets.emergentagent.com/job_ems-reimagined/artifacts/7s94z7x0_7760.png",
  manifesto: "https://customer-assets.emergentagent.com/job_ems-reimagined/artifacts/nhjoddrt_7695.png"
};
